function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import uuid from '../../utils/uuid';
import KEYCODES from '../../utils/KEYCODES';
import Tooltip from '../Tips/Tips';

/**
 * Similarly styled to a tooltip, toggletips are useful when there is supplementary
 * information that should be shared with a user. Ideally this information would
 * not be hidden behind a toggletip and directly visible on the page, however,
 * for circumstances where that is not desired, toggletips to the rescue.
 * A common use case for this is a little `i` or `?` icon next to a form field,
 * clicking it can open a toggletip instead of a modal. Generally this would
 * correspond to what you'd use `aria-describedby` on an input for.
 *
 * Unlike tooltips, toggletips need to be explicitly triggered by a click. To
 * close a toggletip, the user must either remove focus from the triggering
 * button, or click elsewhere on the document. Clicking the button again will
 * not close the toggle tip.
 *
 * @class Toggletip
 */

var TYPE = 'toggletip';
function useValidation(ref) {
  useEffect(function () {
    var el = ref.current;
    if (el) {
      if (!(el.tagName === 'BUTTON' || el.tabIndex === 0)) {
        throw new Error('Toggletip requires the triggering element to be a button or to have ' + 'a tabindex of 0 for accessibility');
      }
    } else {
      throw new Error('A Toggletip requires adding a ref to the component it is attached to');
    }
  }, [ref]);
}
export default function Toggletip(_ref) {
  var i18n = _ref.i18n,
    i18nKey = _ref.i18nKey,
    _ref$direction = _ref.direction,
    direction = _ref$direction === void 0 ? 'bottom' : _ref$direction,
    _ref$neverShow = _ref.neverShow,
    neverShow = _ref$neverShow === void 0 ? false : _ref$neverShow,
    children = _ref.children;
  var ref = useRef(null);
  var tooltipRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    show = _useState2[0],
    setShowing = _useState2[1];
  var _useState3 = useState("".concat(TYPE, "-").concat(uuid())),
    _useState4 = _slicedToArray(_useState3, 1),
    id = _useState4[0];
  var i18nProp = typeof i18nKey !== 'undefined' ? i18nKey : typeof i18n !== 'undefined' ? i18n : '';
  var hideTooltip = useCallback(function () {
    setShowing(false);
  }, []);

  // events
  var onClick = useCallback(function (e) {
    setShowing(true);
  }, []);
  var onKeyDown = useCallback(function (e) {
    if (e.which === KEYCODES.ESC) {
      hideTooltip();
    }
  }, [hideTooltip]);
  var onBlur = useCallback(function (e) {
    var el = ref.current;
    var target = e.relatedTarget;
    var tip = tooltipRef.current;
    if (el && tip && !el.contains(target) && !tip.contains(target)) {
      hideTooltip();
    }
  }, [hideTooltip]);
  useEffect(function () {
    function handleDocumentClick(e) {
      var el = ref.current;
      var target = e.target;
      var tip = tooltipRef.current;
      if (el && tip && !el.contains(target) && !tip.contains(target)) {
        hideTooltip();
      }
    }
    document.addEventListener('click', handleDocumentClick, false);
    return function () {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [hideTooltip]);
  useValidation(ref);
  var childrenProps = useMemo(function () {
    return {
      ref: ref,
      events: {
        onClick: onClick,
        onKeyDown: onKeyDown,
        onBlur: onBlur
      }
    };
  }, [onBlur, onClick, onKeyDown]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, children(childrenProps), /*#__PURE__*/React.createElement(Tooltip, {
    ref: tooltipRef,
    id: id,
    triggerRef: ref,
    show: neverShow ? false : show,
    type: TYPE,
    direction: direction
  }, i18nProp));
}